import { Warning } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import { Avatar, Box, Button, Dialog, DialogActions, DialogTitle, Tooltip, Typography } from '@mui/material';
import { MEIO_JULGAMENTO_PROCESSO } from '@/dominio/MeioJulgamentoProcesso';
import { MEIO_JULGAMENTO_SESSAO } from '@/dominio/MeioJulgamentoSessao';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import ErrorUtil from '../../util/ErrorUtil';
import NumeroProcesso from '../NumeroProcesso';
import PainelFiltros from '../PainelFiltros';
import ReactComponent from '../ReactComponent';
import FiltroRapido from './FiltroRapido';
import OperacoesEmLoteSelecionar from './OperacoesEmLoteSelecionar';
import PainelProcessosOperacoesEmLote from './PainelProcessosOperacoesEmLote';
import IconeTipoJulgamento from './icones/IconeTipoJulgamento';

class TipoJulgamentoLote extends ReactComponent {
    dialogOpen = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            dialogOpen: observable,
            handleClickIcone: action,
            handleCloseDialog: action,
            processosPauta: computed,
            processosSalaSessao: computed,
            processos: computed,
            limparProcessoPesquisa: action,
            processosSelecionaveis: computed,
            processosSelecionados: computed,
            handleClickPresencial: action,
            handleClickVirtual: action,
            handleClickAlterarTipoJulgamento: action,
            desabilitarFuncionalidadeParaLoteEspecifico: computed,
            mensagemLoteSemProcessos: computed,
            tooltipBotao: computed,
            isSessaoExclusivamenteVirtual: computed,
            hasSelecionadosApenasProcessosPresenciais: computed,
            tooltipBotaoPresencial: computed,
            hasSelecionadosApenasProcessosVirtuais: computed,
            tooltipBotaoVirtual: computed
        });

        this.createReaction(
            reaction(
                () => this.dialogOpen,
                () => this.limparProcessoPesquisa()
            )
        );
    }

    handleClickIcone = () => {
        this.dialogOpen = true;
    };

    handleCloseDialog = () => {
        this.dialogOpen = false;
    };

    get processosPauta() {
        return (this.pautaJulgamentoStore.processosPautaSelecionadaFiltrados || []).filter((registro) =>
            this.filtrarProcessoPeloNumero(registro)
        );
    }

    get processosSalaSessao() {
        return (this.store.processosFiltrados || []).filter((registro) => this.filtrarProcessoPeloNumero(registro));
    }

    get processos() {
        return this.isPautaJulgamento ? this.processosPauta : this.processosSalaSessao;
    }

    limparProcessoPesquisa = () => {
        this.processoPesquisa = { numero: '', digito: '', ano: '', orgao: '', regional: '', vara: '' };
    };

    get processosSelecionaveis() {
        return this.processos.filter((registro) => this.isPermitidaAlteracaoTipoJulgamento(registro));
    }

    isPermitidaAlteracaoTipoJulgamento(registro) {
        return this.salaSessaoStore.isPermitidaAlteracaoTipoJulgamento(registro);
    }

    get processosSelecionados() {
        return this.processosSelecionaveis.filter((registro) => this.store.isCheckedOperacoesEmLote(registro));
    }

    handleClickPresencial = () => {
        this.handleClickAlterarTipoJulgamento(MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.codigo);
    };

    handleClickVirtual = () => {
        this.handleClickAlterarTipoJulgamento(MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo);
    };

    handleClickAlterarTipoJulgamento = async (novoTipo) => {
        if (this.processosSelecionados.length > 0) {
            await this.store.processarLote({
                titulo: 'Alterando tipo de julgamento',
                qtdTotalProcessos: this.processosSelecionados.length,
                lotesProcessos: this.processosSelecionados,
                funcaoOperacao: (registro) => this.alterarTipoJulgamento(registro, novoTipo)
            });
        } else {
            this.mensagemStore.abrirMensagemAlerta('Selecione um ou mais processos');
        }
    };

    async alterarTipoJulgamento(registro, novoTipo) {
        let resultado = await this.processoService.alterarTipoJulgamentoLote(registro, novoTipo);

        if (resultado && !ErrorUtil.isResultadoErro(resultado)) {
            runInAction(() => (registro.tipoJulgamento = novoTipo));
        }

        if (this.isPautaJulgamento) {
            this.pautaJulgamentoStore.incrementarProgressoLote();
        } else {
            this.salaSessaoStore.incrementarProgressoLote();
        }

        return resultado;
    }

    getMapaProcessosPorSessao() {
        let mapaSessaoNumerosProcessos = new Map();
        //organiza os processos por sessão
        this.processosSelecionados.forEach((reg) => {
            let numeros = mapaSessaoNumerosProcessos.get(reg.sessaoId);
            if (!numeros) {
                mapaSessaoNumerosProcessos.set(reg.sessaoId, [reg.processo.numeroCompleto]);
            } else {
                numeros.push(reg.processo.numeroCompleto);
            }
        });
        return mapaSessaoNumerosProcessos;
    }

    get desabilitarFuncionalidadeParaLoteEspecifico() {
        return (
            (this.salaSessaoStore.processosSessao || []).filter((registro) =>
                this.isPermitidaAlteracaoTipoJulgamento(registro)
            ).length === 0
        );
    }

    get mensagemLoteSemProcessos() {
        return 'Não há processos na sessão' + this.complementoMensagemFiltrando;
    }

    getAlertasProcesso = (registro) => {
        let alertas = [];

        if (registro.necessitaImportacaoSalaSessao) {
            alertas.push({ mensagem: 'Processo não importado para sala de sessão', impeditivo: false });
        }

        const restricoes = this.salaSessaoStore.verificarCondicoesParaAlteracaoMeioJulgamentoSatisfeitas(registro);

        restricoes.forEach((msg) => {
            alertas.push({
                mensagem: msg,
                impeditivo: true
            });
        });

        return alertas;
    };

    renderTooltipDesabilitado(msg) {
        return (
            <>
                <Warning sx={{ mr: 0.5, verticalAlign: 'middle', color: 'orange' }} />
                <Typography
                    fontSize={'small'}
                    display="inline"
                >
                    {msg}
                </Typography>
            </>
        );
    }

    get tooltipBotao() {
        if (this.salaSessaoStore.isSessaoFinalizada) {
            const msg =
                'Não é possível alterar o tipo de julgamento em sessões presenciais iniciadas ou sessões virtuais finalizadas.';
            return this.renderTooltipDesabilitado(msg);
        }

        if (this.desabilitarFuncionalidadeParaLoteEspecifico) {
            const msg = 'Não há processos que satisfaçam as condições para alteração do tipo de julgamento em lote';
            return this.renderTooltipDesabilitado(msg);
        }

        return <Typography>Alterar meio de julgamento em lote</Typography>;
    }

    get isSessaoExclusivamenteVirtual() {
        return this.sessaoStore?.sessaoSelecionada?.tipoJulgamento === MEIO_JULGAMENTO_SESSAO.VIRTUAL.codigo;
    }

    get hasSelecionadosApenasProcessosPresenciais() {
        return (
            this.processosSelecionados.length > 0 &&
            this.processosSelecionados.every(
                (registro) => registro.tipoJulgamento === MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.codigo
            )
        );
    }

    get tooltipBotaoPresencial() {
        let mensagemTooltipBotaoPresencial = 'Alterar meio de julgamento dos processos selecionados para Presencial';
        if (this.isSessaoExclusivamenteVirtual) {
            mensagemTooltipBotaoPresencial =
                'Não é possível alterar o processo para o meio de julgamento presencial em um sessão com a modalidade de sessão exclusivamente virtual';
        } else if (this.hasSelecionadosApenasProcessosPresenciais) {
            mensagemTooltipBotaoPresencial =
                'Todos os processos selecionados já estão no meio de julgamento presencial';
        }
        return mensagemTooltipBotaoPresencial;
    }

    get hasSelecionadosApenasProcessosVirtuais() {
        return (
            this.processosSelecionados.length > 0 &&
            this.processosSelecionados.every(
                (registro) => registro.tipoJulgamento === MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo
            )
        );
    }

    get tooltipBotaoVirtual() {
        let mensagemTooltipBotaoVirtual = 'Alterar meio de julgamento dos processos selecionados para virtual';
        if (this.hasSelecionadosApenasProcessosVirtuais) {
            mensagemTooltipBotaoVirtual = 'Todos os processos selecionados já estão no meio de julgamento virtual';
        }
        return mensagemTooltipBotaoVirtual;
    }

    render() {
        return (
            <>
                <div style={{ marginRight: '10px', marginBottom: '3px' }}>
                    {this.salaSessaoStore.isSessaoFinalizada || this.desabilitarFuncionalidadeParaLoteEspecifico ? (
                        <Tooltip
                            title={this.tooltipBotao}
                            placement="top-start"
                        >
                            <Avatar
                                sx={{
                                    width: '24px',
                                    height: '24px',
                                    fontWeight: '600',
                                    fontSize: '0.98rem',
                                    backgroundColor: 'black',
                                    opacity: 0.3
                                }}
                            >
                                J
                            </Avatar>
                        </Tooltip>
                    ) : (
                        <Tooltip
                            title={this.tooltipBotao}
                            placement="top-start"
                        >
                            <Avatar
                                onClick={this.handleClickIcone}
                                sx={{
                                    width: '24px',
                                    height: '24px',
                                    fontWeight: '600',
                                    fontSize: '0.98rem',
                                    cursor: 'pointer',
                                    backgroundColor: 'black',
                                    '&:hover': {
                                        backgroundColor: 'steelblue'
                                    }
                                }}
                            >
                                J
                            </Avatar>
                        </Tooltip>
                    )}
                </div>
                {this.dialogOpen && (
                    <Dialog
                        fullWidth
                        maxWidth="xl"
                        onClose={this.handleCloseDialog}
                        open={this.dialogOpen}
                        slotProps={{
                            paper: {
                                sx: { height: '100%' }
                            }
                        }}
                    >
                        <DialogTitle
                            id="simple-dialog-title"
                            style={{ padding: '16px 24px 0px' }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h4 style={{ margin: '0px' }}>Alterar meio de julgamento em lote</h4>
                                </div>
                                <div style={{ margin: '0px 10px' }}>
                                    <NumeroProcesso
                                        dense
                                        tooltip="Número do processo a ser pesquisado na listagem"
                                        callbackOnChange={this.callbackNumeroProcesso}
                                        clickLimpar={this.limparProcessoPesquisa}
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            margin: '10px',
                                            paddingTop: '5px'
                                        }}
                                    >
                                        <PainelFiltros />
                                    </div>
                                    <FiltroRapido id="filtroRapidoTipoJulgamentoLote" />
                                </div>
                            </div>
                        </DialogTitle>
                        <OperacoesEmLoteSelecionar
                            processosSelecionaveis={this.processosSelecionaveis}
                            processos={this.processos}
                        />
                        <PainelProcessosOperacoesEmLote
                            processos={this.processos}
                            mensagemLoteSemProcessos={this.mensagemLoteSemProcessos}
                            alertasProcessos={this.alertasProcessos}
                        />
                        <DialogActions
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                pt: 2,
                                pb: 1,
                                px: 2
                            }}
                        >
                            <Button
                                onClick={this.handleCloseDialog}
                                color="secondary"
                                variant="outlined"
                                endIcon={<CancelIcon sx={{ height: '26px' }} />}
                            >
                                Fechar
                            </Button>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1
                                }}
                            >
                                <Tooltip title={this.tooltipBotaoPresencial}>
                                    <Box>
                                        <Button
                                            onClick={this.handleClickPresencial}
                                            variant="outlined"
                                            endIcon={
                                                <IconeTipoJulgamento
                                                    tipoJulgamento={MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.codigo}
                                                    apenasIcone
                                                />
                                            }
                                            disabled={
                                                this.isSessaoExclusivamenteVirtual ||
                                                this.hasSelecionadosApenasProcessosPresenciais
                                            }
                                        >
                                            Alterar para Presencial
                                        </Button>
                                    </Box>
                                </Tooltip>
                                <Tooltip title={this.tooltipBotaoVirtual}>
                                    <Box>
                                        <Button
                                            onClick={this.handleClickVirtual}
                                            variant="outlined"
                                            endIcon={
                                                <IconeTipoJulgamento
                                                    tipoJulgamento={MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo}
                                                    apenasIcone
                                                />
                                            }
                                            disabled={this.hasSelecionadosApenasProcessosVirtuais}
                                        >
                                            Alterar para Virtual
                                        </Button>
                                    </Box>
                                </Tooltip>
                            </Box>
                        </DialogActions>
                    </Dialog>
                )}
            </>
        );
    }
}

export default inject('stores')(observer(TipoJulgamentoLote));
